import type { ThreadSummary } from "../../../api/threads/threadDecoders";

/**
 * A project's Thread list as the browser follows it on the live socket (Q-62): the server
 * answers a project subscription with the list, then sends each changed line, and each removed
 * Thread, in order. A
 * reconnect subscribes again and gets the whole list, so nothing missed stays missing.
 * Pure, so the rules are tested in one place.
 */
export interface ProjectThreadList {
  readonly projectId: string;
  /** `null` until the first list arrived. */
  readonly threads: readonly ThreadSummary[] | null;
  /** `following` once the list arrived on the open socket; `subscribing` while waiting. */
  readonly delivery: "subscribing" | "following" | "failed";
  /**
   * Counts the times the person's own placements may have changed (F44): each
   * `thread_placements_changed`, and each list on (re)subscribing, as a reconnect may have
   * missed one. Whoever shows placements reads them again when it moves.
   */
  readonly placementsChanged: number;
}

export function subscribingProjectList(projectId: string, previous?: ProjectThreadList): ProjectThreadList {
  return { projectId, threads: previous?.threads ?? null, delivery: "subscribing", placementsChanged: previous?.placementsChanged ?? 0 };
}

/** The list the server sent on (re)subscribing replaces what was held. */
export function receivedThreadList(list: ProjectThreadList, threads: readonly ThreadSummary[]): ProjectThreadList {
  return { ...list, threads: [...threads].sort(byRecentActivity), delivery: "following", placementsChanged: list.placementsChanged + 1 };
}

/** The server said the person's placements in the project changed. */
export function receivedPlacementsChange(list: ProjectThreadList): ProjectThreadList {
  return { ...list, placementsChanged: list.placementsChanged + 1 };
}

/**
 * A changed or new line replaces the Thread's line. The server sends lines in order; a line
 * with an older revision than the one held is ignored all the same (W-22): an approval or a
 * connector's presence moves the revision without an entry.
 */
export function receivedThreadLine(list: ProjectThreadList, changed: ThreadSummary): ProjectThreadList {
  if (list.threads === null) {
    return list; // Before the list: it will contain the change.
  }
  const held = list.threads.find((thread) => thread.threadId === changed.threadId);
  if (held !== undefined && held.projection.revision > changed.projection.revision) {
    return list;
  }
  const others = list.threads.filter((thread) => thread.threadId !== changed.threadId);
  return { ...list, threads: [...others, changed].sort(byRecentActivity) };
}

/** A Run of the list was removed (F158 part 3's erasure): its line goes; a list without it stays as it is. */
export function receivedThreadUnlisted(list: ProjectThreadList, threadId: string): ProjectThreadList {
  if (list.threads === null || !list.threads.some((thread) => thread.threadId === threadId)) {
    return list;
  }
  return { ...list, threads: list.threads.filter((thread) => thread.threadId !== threadId) };
}

/**
 * What the sidebar shows: the followed list while it is current, else the list read over
 * HTTP (before the socket's list arrives, or while the socket is down or refused).
 */
export function shownThreads(
  readOverHttp: readonly ThreadSummary[] | null,
  followed: ProjectThreadList | undefined,
): readonly ThreadSummary[] | null {
  if (followed?.delivery === "following" && followed.threads !== null) {
    return followed.threads;
  }
  return readOverHttp;
}

/** Most recently active first; ties by ID, so the order is stable. */
export function byRecentActivity(left: ThreadSummary, right: ThreadSummary): number {
  const byTime = Date.parse(right.lastEntryAt) - Date.parse(left.lastEntryAt);
  return byTime !== 0 ? byTime : left.threadId.localeCompare(right.threadId);
}
